import React from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import { checkOfferType, formatDate, truncateString } from '../../../../../shared/functions/Functions';
import { MessagesIcon, PlannerIcon, ReportingIcon } from '../../../../../shared/Icons';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { gtmService } from '../../../../../utils/gtm.service';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { UserRoles } from '../../../../../enum/UserRoles';
import { IOfferWithCampaign } from '../../../profile/profile-view';
import PaymentInfoBlock from '../../../../../shared/components/PaymentInfoBlock';
import { PaymentTermsEnum } from '../../../../../enum/PaymenTermsEnum';

interface ICardViewOfferProps {
    offer: IOfferWithCampaign;
}

const CardViewOffer = ({ offer }: ICardViewOfferProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        user: { userType },
    } = useSelector((reduxState: IAllStates) => reduxState.auth);
    const hasStripe = offer?.isLongTerm || offer?.isAddonsOnly || offer?.isOnTop;
    const isAgency = [UserRoles.AGENCY, UserRoles.AGENCY_MASTER].includes(userType as UserRoles);
    const navigateHandler = (route: string, campaignId: number, publishFrom?: string) => {
        navigate(route, {
            state: {
                campaignId,
                publishFrom,
            },
        });
    };

    const navigateToCampaignOverview = (campaignId: number) => {
        isAgency
            ? navigate(`/agency/campaign/${campaignId}/overview`)
            : navigate(`/influencer/campaign/${campaignId}/overview`);
        gtmService('select_content');
    };

    return (
        <Card className="p-3 h-100 d-flex flex-column justify-content-between overflow-hidden">
            <div className="d-flex align-items-start justify-content-between">
                <div className="position-relative cursor-pointer w-40">
                    <div className="position-relative">
                        <CustomImageHandler
                            style={{ height: 130 }}
                            onClick={() => navigateToCampaignOverview(offer?.campaign?.id)}
                            classes="img-cover default-radius-sm w-100 shadow"
                            thumbnailPath={offer?.campaign?.campaignImages?.[0]?.thumbnail}
                            photoPath={offer?.campaign?.campaignImages?.[0]?.thumbnail}
                            altTag="Campaign logo"
                        />
                        {hasStripe && (
                            <span className={`status-badge ${hasStripe ? 'linked' : 'long'}`}>
                                {t(
                                    `brand.campaign.briefing.overview_card.${checkOfferType(
                                        offer?.isLongTerm,
                                        offer?.isOnTop,
                                        offer?.isAddonsOnly
                                    )}`
                                )}
                            </span>
                        )}
                    </div>
                    {/*{!isInvoice && (*/}
                    {/*    <span*/}
                    {/*        className={`status campaign-status-dashboard ${*/}
                    {/*            offer?.campaign?.status ?? offer?.campaign?.offers[0]?.status*/}
                    {/*        } position-absolute`}*/}
                    {/*    >*/}
                    {/*                        {t(*/}
                    {/*                            `general.campaign_status.${*/}
                    {/*                                offer?.campaign?.status ?? offer?.status*/}
                    {/*                            }`,*/}
                    {/*                        )}*/}
                    {/*                </span>*/}
                    {/*)}*/}
                    <div className="d-flex align-items-center justify-content-between mt-3">
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/planner' : '/influencer/planner',
                                    offer?.campaign?.id,
                                    offer?.campaign?.publishFrom
                                )
                            }
                            className="cursor-pointer  d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <PlannerIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.planner')}</span>
                            {offer?.redDots?.plannerDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/reporting' : '/influencer/reporting',
                                    offer?.campaign?.id
                                )
                            }
                            className="cursor-pointer   d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <ReportingIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.reporting')}</span>
                            {offer?.redDots?.reportingDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                        <div
                            onClick={() =>
                                navigateHandler(
                                    isAgency ? '/agency/messages' : '/influencer/messages',
                                    offer?.campaign?.id
                                )
                            }
                            className="cursor-pointer d-flex align-items-center flex-column justify-content-center position-relative"
                        >
                            <MessagesIcon width="20" height="20" />
                            <span className="font-10">{t('influencer.campaign.tabs.messages')}</span>
                            {offer?.redDots?.messagesDot && <div id="bullet" className="offer-bullet notify-dot" />}
                        </div>
                    </div>
                </div>
                <div className="w-60 pl-md-3 pl-2 d-flex flex-column h-100 justify-content-between">
                    <div className="d-flex flex-column ">
                        <h5
                            className="cursor-pointer mb-0 "
                            onClick={() => navigateToCampaignOverview(offer?.campaign?.id)}
                        >
                            {truncateString(offer?.campaign?.title ?? '-', 40)}
                        </h5>
                        <span className="text-primary">{offer?.campaign?.company?.displayName ?? '-'}</span>
                    </div>
                    {(offer?.isLongTerm || offer?.isAddonsOnly) && (
                        <span className="text-muted">
                            {t('brand.campaign.book.offer.part_of_name', {
                                campaign: offer?.campaign?.parent?.title ?? '-',
                            })}
                        </span>
                    )}
                    <div className="mb-2">
                        <div>{t('influencer.explore.publicationPeriod')} :</div>
                        <div className="text-primary font-weight-bold">
                            {formatDate(offer?.campaign?.publishFrom ?? '') +
                                ' - ' +
                                formatDate(offer?.campaign?.publishTo)}
                        </div>
                    </div>

                    <div className="d-flex flex-column mt-2 justify-content-end">
                        <div className="d-flex flex-row-reverse justify-content-between">
                            <PaymentInfoBlock
                                main={t(
                                    `brand.campaign.briefing.step8.payment_terms_type.${
                                        offer?.campaign?.paymentTerms === PaymentTermsEnum.OTHER
                                            ? 'custom'
                                            : offer?.campaign?.paymentTerms
                                    }`
                                )}
                                label={`${t('brand.campaign.briefing.step8.payment_terms')}:`}
                                showTooltip={offer?.campaign?.paymentTerms === PaymentTermsEnum.OTHER}
                                tooltipContent={offer?.campaign?.paymentTermsCustom ?? ''}
                                tooltipId="payment_tooltip"
                                mainClassName="font-weight-bold text-primary"
                                labelClass="text-muted mb-2"
                            />
                        </div>
                        <div className="d-flex justify-content-between">
                            <p className="text-muted mb-1">
                                {!offer?.campaign.isCharity && offer?.isLongTerm
                                    ? t('brand.campaign.book.offer.modal.value')
                                    : t('brand.campaign.book.offer.modal.price')}
                            </p>
                            <span
                                className={`${
                                    !offer?.campaign.isCharity && 'euro-symbol'
                                } font-weight-bold text-primary`}
                            >
                                {offer?.campaign.isCharity
                                    ? t('brand.account.price_list_modal.barter_deals')
                                    : priceSplitter(Math.round(offer?.price ?? 0))}
                            </span>
                        </div>
                        <div>
                            <div className="d-flex justify-content-between">
                                <p className="text-muted mb-0">{t('influencer.explore.contentPieces')}</p>
                                <span className="font-weight-bold text-primary">{offer?.totalNbPosts ?? 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default CardViewOffer;
